import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { CoachingSession } from "@shared/types";

interface CoachingSessionsProps {
  userId: string;
  organizationId?: string;
}

export function CoachingSessions({ userId, organizationId }: CoachingSessionsProps) {
  // Build API URL with organization parameter for multi-organization support
  const buildApiUrl = () => {
    let url = `/api/coaching-logs?coachId=${userId}&upcoming=true`;
    if (organizationId) {
      url += `&organizationId=${organizationId}`;
    }
    return url;
  };

  // This would be a real API call in production
  const { data: sessions, isLoading } = useQuery<CoachingSession[]>({
    queryKey: [buildApiUrl(), organizationId],
  });

  const data = sessions || [];

  return (
    <Card className="p-4">
      <CardHeader className="p-0 pb-4 flex justify-between items-center">
        <CardTitle className="text-lg font-medium">Upcoming Coaching</CardTitle>
        <Button asChild variant="ghost" size="sm" className="h-8 w-8 p-0">
          <a href="/coaching" aria-label="View coaching records">
          <span className="mdi mdi-calendar-plus text-neutral-500"></span>
          </a>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, index) => (
              <Skeleton key={index} className="h-16 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {data.length === 0 && <p className="text-sm text-slate-600 dark:text-slate-300">No coaching sessions scheduled.</p>}
            {data.map((session) => (
              <div 
                key={session.id} 
                className={`border-l-4 ${session.isToday ? 'border-primary' : 'border-neutral-300 dark:border-neutral-600'} pl-3 py-1`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-sm">{session.employeeName}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{session.sessionType}</p>
                  </div>
                  <div 
                    className={`text-xs ${
                      session.isToday 
                        ? 'bg-primary bg-opacity-10 text-primary' 
                        : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                    } px-2 py-1 rounded`}
                  >
                    {session.dateTime}
                  </div>
                </div>
              </div>
            ))}
            
            <Button asChild variant="ghost" className="mt-2 w-full">
              <a href="/coaching">
              <span className="mdi mdi-plus mr-1"></span>
              View Coaching Records
              </a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
